// ─── HADIAH PEMBELIAN — "Tambah Hadiah" di keranjang admin (helper MURNI) ───
//
// RENCANA-hadiah-pembelian.md §15.1 (Tahap 3A, mig 8830; putusan pemilik
// §15.8: H30 menurut aturan saja). Server menentukan baris syarat milik setiap
// aturan (`admin_gift_offer_rows`: pemilik H24 + periode per waktu baris
// dipesan, level pelanggan, belum terikat hadiah/bundel) dan menegakkan semua
// syarat lagi saat simpan (`admin_add_order_gift`). Di sini: hak dari baris
// yang DICENTANG admin (cermin `gift_entitlement_measure`), kekurangan, sisa
// kuota pelanggan, pilihan varian hadiah, muatan RPC, dan pesan galat.

import {
  giftEntitlementMeasure,
  giftMissingAmount,
  giftMissingPcs,
  giftQuotaCap,
  giftRewardChoices,
  giftRuleSentence,
  lockedGiftReward,
  normalizeGiftClaim,
  type GiftClaimLine,
  type GiftMeasure,
  type GiftQuota,
  type GiftRewardChoice,
  type GiftRule
} from "@/lib/gift";
import {
  giftRewardName,
  giftTriggerName,
  type GiftCarry,
  type GiftNewPurchase,
  type GiftOpenPurchase,
  type GiftProductInfo
} from "@/lib/giftCart";
import { mapOrderGiftRule, type OrderGiftRule } from "@/lib/giftOrders";
import { isOrderableStatus } from "@/lib/products";

/** Satu baris syarat tawaran (RPC `admin_gift_offer_rows`, mig 8830). */
export type AdminGiftOfferRow = {
  ruleId: string;
  itemId: string;
  orderId: string;
  productId: string;
  variantId: string | null;
  qty: number;
  /** Harga ditagih sebelum voucher per pcs (H25); null = harga menyusul (tak dihitung ukuran nilai). */
  unitValue: number | null;
  orderedAt: string;
  /** Label lembar Tambah Hadiah (baris terkirim tak tampil di keranjang). */
  productName: string;
  variantName: string;
  orderCode: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapAdminGiftOfferRow(row: any): AdminGiftOfferRow {
  const qty = Number(row?.qty);
  const value = row?.unit_value === null || row?.unit_value === undefined ? null : Number(row.unit_value);
  return {
    ruleId: String(row?.rule_id ?? ""),
    itemId: String(row?.item_id ?? ""),
    orderId: String(row?.order_id ?? ""),
    productId: String(row?.product_id ?? ""),
    variantId: row?.variant_id ? String(row.variant_id) : null,
    qty: Number.isFinite(qty) ? Math.max(0, Math.floor(qty)) : 0,
    unitValue: value !== null && Number.isFinite(value) ? Math.round(value) : null,
    orderedAt: String(row?.ordered_at ?? ""),
    productName: String(row?.product_name ?? "").trim(),
    variantName: String(row?.variant_name ?? "").trim(),
    orderCode: String(row?.order_code ?? "").trim()
  };
}

/** Sisa kuota pelanggan tertentu (RPC `admin_gift_rule_quota`, mig 8830). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapAdminGiftQuotaRow(row: any): { ruleId: string; quota: GiftQuota } {
  const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null;
  };
  return {
    ruleId: String(row?.rule_id ?? ""),
    quota: {
      remainingTotal: num(row?.remaining_total),
      perCustomerLimit: num(row?.per_customer_limit),
      remainingForMe: num(row?.remaining_for_customer)
    }
  };
}

export type AdminGiftOffer = {
  rule: GiftRule;
  reward: GiftProductInfo;
  /** Kalimat aturan; nama hadiah = varian dikunci bila ada. */
  sentence: string;
  measure: GiftMeasure;
  /** Baris syarat milik aturan ini, urut waktu dipesan. */
  rows: AdminGiftOfferRow[];
  /** Sisa kuota untuk pelanggan ini (total ∧ jatahnya); null = tak terbatas. */
  remaining: number | null;
  choices: GiftRewardChoice[];
  locked: GiftRewardChoice | null;
};

/**
 * Tawaran per aturan dari baris server. Aturan yang hadiahnya tak terbaca tak
 * ditawarkan (gagal-tutup — server tetap menolak). Urut nama aturan.
 */
export function adminGiftOffers(input: {
  rules: readonly GiftRule[];
  rows: readonly AdminGiftOfferRow[];
  products: ReadonlyMap<string, GiftProductInfo>;
  quotas: ReadonlyMap<string, GiftQuota>;
}): AdminGiftOffer[] {
  const byRule = new Map<string, AdminGiftOfferRow[]>();
  for (const row of input.rows) {
    if (!row.ruleId || !row.itemId || row.qty <= 0) continue;
    const list = byRule.get(row.ruleId);
    if (list) list.push(row);
    else byRule.set(row.ruleId, [row]);
  }
  const out: AdminGiftOffer[] = [];
  for (const rule of input.rules) {
    const rows = byRule.get(rule.id);
    if (!rows?.length || !rule.rewardProductId) continue;
    const reward = input.products.get(rule.rewardProductId);
    if (!reward) continue;
    const choices = giftRewardChoices(rule, reward.variants, reward.optionGroupOf, 1);
    const locked = lockedGiftReward(choices);
    const trigger = giftTriggerName(rule, input.products);
    out.push({
      rule,
      reward,
      sentence:
        trigger === null
          ? rule.name
          : giftRuleSentence(rule, { trigger, reward: giftRewardName(rule, reward, locked) }),
      measure: rule.triggerMeasure,
      rows: [...rows].sort((a, b) => a.orderedAt.localeCompare(b.orderedAt) || a.itemId.localeCompare(b.itemId)),
      remaining: giftQuotaCap(input.quotas.get(rule.id)),
      choices,
      locked
    });
  }
  return out.sort((a, b) => a.rule.name.localeCompare(b.rule.name, "id"));
}

export type AdminGiftStatus =
  /** Hak ada & varian terpilih — bisa disimpan. */
  | "siap"
  /** Baris yang dicentang belum memenuhi syarat. */
  | "kurang"
  /** Hak ada, tapi tak ada varian hadiah yang bisa diberikan (stok habis / Harga Tambahan). */
  | "habis"
  /** Produk hadiah sedang tak bisa dipesan. */
  | "tidak_bisa_dipesan"
  /** Kuota total / jatah pelanggan ini sudah habis. */
  | "kuota_habis";

export type AdminGiftSelection = {
  /** Baris yang dicentang DAN dihitung (ukuran nilai: yang berharga saja) — dikirim ke server. */
  itemIds: string[];
  triggerPcs: number;
  /** Rupiah syarat (harga ditagih sebelum voucher) baris yang dihitung. */
  triggerValue: number;
  /** Ukuran nilai: pcs dicentang yang harganya belum ada — tak dihitung. */
  unpricedPcs: number;
  /** Hak pcs hadiah, SUDAH dijepit sisa kuota (H15/H33). */
  entitlement: number;
  quotaCapped: boolean;
  /** Pcs yang masih kurang menuju hak berikutnya (ukuran pcs); 0 = tak ada. */
  missing: number;
  /** Rupiah yang masih kurang menuju hak berikutnya (ukuran nilai); 0 = tak ada. */
  missingAmount: number;
  status: AdminGiftStatus;
  /** Pilihan varian hadiah (dirapikan terhadap hak & stok; bawaan bila kosong). */
  claim: GiftClaimLine[];
};

/**
 * Hak dari baris yang dicentang (`selected` null = semua baris tawaran) dengan
 * rumus server (`gift_entitlement_measure`). `claim` = pilihan admin (boleh
 * basi — dirapikan `normalizeGiftClaim`).
 */
export function adminGiftSelection(
  offer: AdminGiftOffer,
  selected: ReadonlySet<string> | null,
  claim?: readonly GiftClaimLine[] | null
): AdminGiftSelection {
  const rows = selected ? offer.rows.filter((r) => selected.has(r.itemId)) : offer.rows;
  const nilai = offer.measure === "nilai";
  let pcs = 0;
  let value = 0;
  let unpricedPcs = 0;
  const itemIds: string[] = [];
  for (const row of rows) {
    const priced = row.unitValue !== null && row.unitValue > 0;
    if (nilai && !priced) {
      unpricedPcs += row.qty;
      continue;
    }
    pcs += row.qty;
    if (priced) value += row.qty * (row.unitValue as number);
    itemIds.push(row.itemId);
  }
  const full = giftEntitlementMeasure(offer.rule, pcs, value);
  const remaining = offer.remaining;
  const entitlement = remaining === null ? full : Math.min(full, remaining);
  const quotaCapped = remaining !== null && full > remaining;
  const capped = remaining !== null && full >= remaining;
  const missing = !nilai && !capped ? giftMissingPcs(pcs, offer.rule) : 0;
  const missingAmount = nilai && !capped ? giftMissingAmount(value, offer.rule) : 0;

  let status: AdminGiftStatus;
  let next: GiftClaimLine[] = [];
  if (remaining === 0) status = "kuota_habis";
  else if (entitlement <= 0) status = "kurang";
  else if (!isOrderableStatus(offer.reward.status, offer.reward.productType)) status = "tidak_bisa_dipesan";
  else {
    next = normalizeGiftClaim(claim ?? [], entitlement, offer.choices);
    status = next.length ? "siap" : "habis";
  }
  return {
    itemIds,
    triggerPcs: pcs,
    triggerValue: Math.round(value),
    unpricedPcs,
    entitlement,
    quotaCapped,
    missing,
    missingAmount,
    status,
    claim: next
  };
}

/**
 * Sisa pesanan LAIN per aturan dari baris tawaran (`admin_gift_offer_rows` —
 * pemilik H24 yang sama dengan mesin gabungan, mig 8880): bahan pratinjau
 * pembelian baru Edit Variasi & Gabung Pesanan (H59). Baris pesanan yang
 * dikecualikan dihitung dari editor — satu pesanan (Edit Variasi) atau tujuan
 * + semua duplikat (Gabung Pesanan, Tahap 6-1).
 */
export function adminGiftOfferCarry(
  offers: readonly AdminGiftOffer[],
  excludeOrderIds: string | readonly string[] | null
): Map<string, GiftCarry> {
  const skip = new Set(
    excludeOrderIds === null ? [] : typeof excludeOrderIds === "string" ? [excludeOrderIds] : excludeOrderIds
  );
  const out = new Map<string, GiftCarry>();
  for (const offer of offers) {
    let pcs = 0;
    let pricedPcs = 0;
    let value = 0;
    for (const row of offer.rows) {
      if (skip.has(row.orderId)) continue;
      pcs += row.qty;
      if (row.unitValue !== null && row.unitValue > 0) {
        pricedPcs += row.qty;
        value += row.qty * row.unitValue;
      }
    }
    if (pcs > 0) out.set(offer.rule.id, { pcs, pricedPcs, value });
  }
  return out;
}

/**
 * Pembelian berhadiah pelanggan untuk pratinjau admin
 * (`admin_gift_customer_purchases`, Hadiah Tahap 6-2): hak & pcs hidup dari
 * server, pesanan barang syaratnya, penanda bisa bertambah (kandidat langkah
 * a': hadiah > 0 dan ≤ hak), dan id baris tak terikat yang DICAKUP snapshot-nya
 * — cakupan dihitung server, bahan langkah a & a'. Urut tertua dulu.
 */
export type AdminGiftPurchase = {
  id: string;
  ruleId: string;
  createdAt: string;
  rule: OrderGiftRule;
  orderIds: string[];
  syaratPcs: number;
  syaratValue: number;
  hak: number;
  hadiahPcs: number;
  canGrow: boolean;
  coveredItemIds: ReadonlySet<string>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapAdminGiftPurchaseRow(row: any): AdminGiftPurchase | null {
  const id = String(row?.order_gift_id ?? "");
  const ruleId = String(row?.rule_id ?? "");
  if (!id || !ruleId) return null;
  const whole = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  };
  const ids = (v: unknown) => (Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean) : []);
  return {
    id,
    ruleId,
    createdAt: String(row?.created_at ?? ""),
    rule: mapOrderGiftRule(row?.rule),
    orderIds: ids(row?.order_ids),
    syaratPcs: whole(row?.syarat_pcs),
    syaratValue: whole(row?.syarat_nilai),
    hak: whole(row?.hak),
    hadiahPcs: whole(row?.hadiah_pcs),
    canGrow: row?.can_grow === true,
    coveredItemIds: new Set(ids(row?.covered_item_ids))
  };
}

/** Pcs / pcs berharga / nilai baris tawaran — ukuran nilai aturan HIDUP: baris berharga saja (cermin bahan mesin). */
function offerRowsMeasure(rows: readonly AdminGiftOfferRow[], nilai: boolean): GiftCarry {
  let pcs = 0;
  let pricedPcs = 0;
  let value = 0;
  for (const row of rows) {
    const priced = row.unitValue !== null && row.unitValue > 0;
    if (nilai && !priced) continue;
    pcs += row.qty;
    if (priced) {
      pricedPcs += row.qty;
      value += row.qty * (row.unitValue as number);
    }
  }
  return { pcs, pricedPcs, value };
}

/**
 * Pembelian LAMA yang masih bisa bertambah per aturan (bentuk `giftOpenByRule`)
 * untuk pratinjau editor admin (Edit Variasi, Gabung Pesanan): sisa pesanan lain
 * = baris tawaran milik aturan pembelian itu (H24) yang dicakup snapshot-nya, di
 * luar pesanan `excludeOrderIds` (dihitung editor). Pembelian yang barang
 * syaratnya ada di pesanan itu dimodelkan langkah (a) editor, bukan (a').
 */
export function adminGiftOpenPurchases(
  purchases: readonly AdminGiftPurchase[],
  offers: readonly AdminGiftOffer[],
  excludeOrderIds: readonly string[]
): Map<string, GiftOpenPurchase[]> {
  const skip = new Set(excludeOrderIds);
  const offerOf = new Map(offers.map((o) => [o.rule.id, o] as const));
  const out = new Map<string, GiftOpenPurchase[]>();
  for (const p of purchases) {
    if (!p.canGrow || p.orderIds.some((o) => skip.has(o))) continue;
    const offer = offerOf.get(p.ruleId);
    const rows = (offer?.rows ?? []).filter((r) => !skip.has(r.orderId) && p.coveredItemIds.has(r.itemId));
    const open: GiftOpenPurchase = {
      id: p.id,
      ruleId: p.ruleId,
      createdAt: p.createdAt,
      rule: p.rule,
      orderIds: p.orderIds,
      syaratPcs: p.syaratPcs,
      syaratValue: p.syaratValue,
      hak: p.hak,
      hadiahPcs: p.hadiahPcs,
      carry: offerRowsMeasure(rows, offer?.measure === "nilai")
    };
    out.set(p.ruleId, [...(out.get(p.ruleId) ?? []), open]);
  }
  return out;
}

/**
 * Langkah (a) mesin (mig 8860/8880) untuk Ubah Pcs: baris tak terikat
 * `itemIds` di pesanan `orderId` ikut pembelian TERTUA di pesanan itu yang
 * snapshot-nya mencakupnya (cakupan dari server). id baris → id pembelian.
 */
export function adminGiftOrderJoins(
  purchases: readonly AdminGiftPurchase[],
  orderId: string,
  itemIds: readonly string[]
): Map<string, string> {
  const here = purchases.filter((p) => p.orderIds.includes(orderId));
  const out = new Map<string, string>();
  for (const itemId of itemIds) {
    const p = here.find((x) => x.coveredItemIds.has(itemId));
    if (p) out.set(itemId, p.id);
  }
  return out;
}

/**
 * Pratinjau sesudah Ubah Pcs NAIK pada baris tak terikat `itemId` (H59, mig
 * 8880; Hadiah Tahap 6-2): langkah (a') — pembelian LAMA aturan pemilik baris
 * itu yang haknya naik menyerap baris tawaran yang dicakup snapshot-nya
 * (`old: true`) — lalu langkah (b') — sisanya jadi pembelian BARU bila pesanan
 * baris itu ikut menyumbang dan haknya > 0. Baris yang ikut pembelian di
 * pesanan yang sama (langkah a, `joined`) bukan bahan keduanya; baris itu
 * sendiri ikut (a) → [] (pratinjau pembelian itu yang menuliskannya). Tanpa
 * `purchases` (belum termuat): pesanan berhadiah → [] (gagal-tutup).
 */
export function adminGiftQtyNewPurchases(input: {
  offers: readonly AdminGiftOffer[];
  itemId: string;
  qty: number;
  /** Pembelian pelanggan (`admin_gift_customer_purchases`); absen = belum termuat. */
  purchases?: readonly AdminGiftPurchase[];
  /** Baris tak terikat yang ikut pembelian di pesanan yang sama (langkah a). */
  joined?: ReadonlySet<string>;
  /** Pesanan baris itu sudah memuat barang syarat pembelian berhadiah. */
  orderHasPurchase: boolean;
}): GiftNewPurchase[] {
  if (!input.purchases && input.orderHasPurchase) return [];
  const joined = input.joined ?? new Set<string>();
  if (joined.has(input.itemId)) return [];
  const offer = input.offers.find((o) => o.rows.some((r) => r.itemId === input.itemId));
  if (!offer) return [];
  const row = offer.rows.find((r) => r.itemId === input.itemId)!;
  const qty = Number.isFinite(input.qty) ? Math.max(0, Math.floor(input.qty)) : 0;
  if (qty <= row.qty) return [];
  const nilai = offer.measure === "nilai";
  let material = offer.rows
    .filter((r) => !joined.has(r.itemId))
    .map((r) => (r.itemId === input.itemId ? { ...r, qty } : r));
  const out: GiftNewPurchase[] = [];
  let remaining = offer.remaining;
  for (const p of input.purchases ?? []) {
    if (!p.canGrow || p.ruleId !== offer.rule.id) continue;
    const covered = material.filter((r) => p.coveredItemIds.has(r.itemId));
    const m = offerRowsMeasure(covered, nilai);
    const joinPcs = (p.rule.triggerMeasure ?? "pcs") === "nilai" ? m.pricedPcs : m.pcs;
    if (joinPcs <= 0) continue;
    const hakAfter = giftEntitlementMeasure(p.rule, p.syaratPcs + joinPcs, p.syaratValue + m.value);
    if (hakAfter <= p.hak) continue;
    const pcs = remaining === null ? hakAfter - p.hak : Math.min(hakAfter - p.hak, remaining);
    if (pcs > 0) out.push({ ruleId: offer.rule.id, name: offer.rule.name, pcs, old: true });
    if (remaining !== null) remaining = Math.max(0, remaining - pcs);
    const used = new Set(covered.map((r) => r.itemId));
    material = material.filter((r) => !used.has(r.itemId));
    break;
  }
  // (b') hanya bila pesanan baris itu ikut menyumbang barang yang tersisa.
  if (!material.some((r) => r.orderId === row.orderId)) return out;
  const sel = adminGiftSelection({ ...offer, rows: material, remaining }, null);
  if (sel.status === "siap" && sel.entitlement > 0) {
    out.push({ ruleId: offer.rule.id, name: offer.rule.name, pcs: sel.entitlement });
  }
  return out;
}

/** Muatan RPC `admin_add_order_gift`; null = belum bisa disimpan. */
export function adminGiftPayload(
  customerId: string,
  offer: AdminGiftOffer,
  selection: AdminGiftSelection
): { p_customer_id: string; p_rule_id: string; p_item_ids: string[]; p_lines: Array<{ variant_id: string; qty: number }> } | null {
  if (selection.status !== "siap" || !selection.itemIds.length) return null;
  const lines = selection.claim
    .filter((l) => l.variantId && Number.isFinite(l.qty) && l.qty > 0)
    .map((l) => ({ variant_id: l.variantId, qty: Math.floor(l.qty) }));
  if (!lines.length) return null;
  return { p_customer_id: customerId, p_rule_id: offer.rule.id, p_item_ids: selection.itemIds, p_lines: lines };
}

/**
 * Baris tempat tombol Tambah Hadiah menempel: baris syarat TERAKHIR (urutan
 * tampil) di kartu pertama yang memuat baris tawaran aturan itu — aturan
 * penempatan yang sama dengan hadiah yang sudah ada (`nestGiftRows`).
 * `order` = id baris per kartu dalam urutan tampil. null = tak satu pun baris
 * tawaran sedang tampil (mis. tab lain) — tombolnya tak dipasang di baris.
 */
export function adminGiftHostItem(
  offer: Pick<AdminGiftOffer, "rows">,
  order: readonly (readonly string[])[]
): string | null {
  const mine = new Set(offer.rows.map((r) => r.itemId));
  for (const card of order) {
    let host: string | null = null;
    for (const id of card) if (mine.has(id)) host = id;
    if (host) return host;
  }
  return null;
}

/**
 * Pesan galat Tambah Hadiah untuk admin. `raw` = pesan + "|" + detail galat
 * server (nama aturan). null = bukan galat hadiah — pemanggil memajang pesan
 * server apa adanya (mis. izin).
 */
export function adminGiftErrorMessage(raw: string | null | undefined): string | null {
  const m = String(raw ?? "").match(/GIFT_INVALID:([a-z_]+)(?:\|(.*))?/);
  if (!m) return null;
  const name = (m[2] ?? "").trim();
  const rule = name ? `aturan "${name}"` : "aturan ini";
  switch (m[1]) {
    case "inactive":
      return `Hadiah ${rule} sudah dinonaktifkan. Muat ulang keranjang.`;
    case "level":
      return `Level pelanggan ini tidak termasuk ${rule}.`;
    case "unavailable":
      return `Produk hadiah ${rule} sedang tidak bisa dipesan.`;
    case "syarat":
      return `Barang syaratnya sudah dipakai hadiah lain atau tidak lagi masuk ${rule}. Muat ulang keranjang.`;
    case "not_eligible":
      return `Barang yang dicentang belum memenuhi syarat ${rule}.`;
    case "variant":
      return "Varian hadiahnya sudah tidak tersedia. Pilih varian lain.";
    case "price_extra":
      return "Varian ber-Harga Tambahan tidak bisa jadi hadiah. Pilih varian lain.";
    case "stock":
      return "Stok hadiahnya tidak cukup. Kurangi jumlahnya atau pilih varian lain.";
    case "quota":
      return `Sisa kuota ${rule} tidak cukup. Muat ulang keranjang.`;
    // Lengkapi Hadiah (mig 8870): kekurangannya sudah terisi / pembelian tak ada.
    case "full":
      return `${name ? `Hadiah "${name}"` : "Hadiahnya"} sudah lengkap. Muat ulang keranjang.`;
    case "purchase":
      return "Pembelian berhadiah ini tidak ditemukan. Muat ulang keranjang.";
    default:
      return "Jumlah hadiah melebihi hak pelanggan ini. Muat ulang keranjang.";
  }
}
